/**
 * React Query mutation hook for setting a primary Google account for an org.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { fetchWithDefaults } from '@/lib/http/client';

export interface SetPrimaryParams {
  orgId: string;
  socialAccountId: number;
}

interface SetPrimaryResponse {
  success?: boolean;
  message?: string;
}

async function setPrimaryAccount(
  params: SetPrimaryParams
): Promise<SetPrimaryResponse> {
  const response = await fetchWithDefaults(
    `/api/orgs/${params.orgId}/google/set-primary/`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ social_account_id: params.socialAccountId }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || `Set primary failed: ${response.status}`
    );
  }

  return response.json();
}

export function useSetPrimaryAccount() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: setPrimaryAccount,
    onSuccess: () => {
      // Invalidate Gmail status queries to refresh primary account status
      queryClient.invalidateQueries({
        queryKey: ['google', 'gmail', 'status'],
      });
    },
  });

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    variables: mutation.variables,
  };
}
